import { isEqual } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';

import { CandidateFilterType } from '@pflegia/types/api/candidate/candidate-filter-type';
import { TypeOfWork } from '@pflegia/types/api/candidate/type-of-work';
import { FilterValue } from '@pflegia/types/api/job-board/filter-value';
import { LocationObject } from '@pflegia/types/common/location/location-object';

import { useFiltersState } from 'domains/job-board/application/use-filter-state.hook';
import useJobTitlesFilterList from 'domains/job-board/application/use-job-titles-filter-list.hook';

interface Props {
  location?: LocationObject;
  jobTitleId?: string;
  jobTitleIds?: string[];
  isJobTitleResolved?: boolean;
  typeOfWork?: TypeOfWork;
  isAuthenticated?: boolean;
  isUrlFilterSyncSkipped?: boolean;
}

const useInitializeFilters = ({
  jobTitleId,
  jobTitleIds,
  isJobTitleResolved = false,
  location,
  typeOfWork,
  isAuthenticated = false,
  isUrlFilterSyncSkipped = false,
}: Props) => {
  const { jobTitles, isLoading } = useJobTitlesFilterList();
  const { filters: existingFilters, setFilters, isInitialized, filtersFullyInitialized, setFiltersFullyInitialized } = useFiltersState();
  const lastUrlFiltersRef = useRef<FilterValue[] | null>(null);
  const resolvedJobTitleId = isJobTitleResolved ? jobTitleId : jobTitles.find(({ value }) => value === jobTitleId)?.value;
  const shouldWaitForJobTitleLookup = Boolean(jobTitleId && !isJobTitleResolved && isLoading);

  const urlParams = useMemo(
    () => ({
      jobTitleValue: resolvedJobTitleId,
      locationAddress: location?.formattedAddress,
      typeOfWork,
    }),
    [resolvedJobTitleId, location?.formattedAddress, typeOfWork],
  );

  useEffect(() => {
    if (isAuthenticated || shouldWaitForJobTitleLookup || isInitialized) return;

    const urlFilters: FilterValue[] = [];

    const positionIds = jobTitleIds ?? (resolvedJobTitleId ? [resolvedJobTitleId] : []);
    positionIds.forEach(value => urlFilters.push({ type: CandidateFilterType.Position, value }));

    if (location) {
      urlFilters.push({
        locationObject: location,
        type: CandidateFilterType.Location,
        value: location.formattedAddress,
      });
    }

    if (typeOfWork) {
      urlFilters.push({ type: CandidateFilterType.Facility, value: typeOfWork });
    }

    // Internal route sync only reflects the canonical first-filter pair in the URL.
    // Record that URL state so it is not treated as a new navigation, so keeping any
    // extra UI-only filters instead of overriding them with the filter state from the URL.
    if (isUrlFilterSyncSkipped && urlFilters.length > 0) {
      lastUrlFiltersRef.current = urlFilters;
      return;
    }

    if (urlFilters.length > 0) {
      const hasUrlFiltersChanged = !isEqual(lastUrlFiltersRef.current, urlFilters);
      lastUrlFiltersRef.current = urlFilters;

      if ((!filtersFullyInitialized || hasUrlFiltersChanged) && !isEqual(existingFilters, urlFilters)) {
        setFilters(urlFilters, true);
      } else if (!filtersFullyInitialized) {
        setFiltersFullyInitialized(true);
      }
      return;
    }

    if (filtersFullyInitialized) return;

    lastUrlFiltersRef.current = null;
    const filters = [...existingFilters];
    if (filters.length > 0) {
      setFilters(filters, true);
    } else if (!filtersFullyInitialized) {
      setFiltersFullyInitialized(true);
    }
  }, [
    resolvedJobTitleId,
    jobTitleIds,
    urlParams,
    location,
    setFilters,
    typeOfWork,
    shouldWaitForJobTitleLookup,
    isAuthenticated,
    filtersFullyInitialized,
    setFiltersFullyInitialized,
    isInitialized,
    existingFilters,
    isUrlFilterSyncSkipped,
  ]);

  return { isInitialized };
};

export default useInitializeFilters;
